import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GitBranch, Loader2 } from "lucide-react";
import { toast } from "sonner";

const RECALL_BG = {
  Recalled: "bg-red-50 text-red-700 border-red-300",
  Superseded: "bg-amber-50 text-amber-700 border-amber-300",
};
const TYPE_LABEL = {
  source_item: "Source item", hcp_statement: "HCP statement", scientific_evidence_record: "Evidence record",
  finding: "Finding", consensus_assessment: "Consensus", emerging_signal: "Signal", score: "Score",
  strategic_implication: "Implication", report_claim: "Report claim",
};

export const CorrectionCascade = () => {
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [busyFlag, setBusyFlag] = useState(null);

  const load = () => {
    setLoadError(false);
    api.get("/cascade").then((r) => setStatus(r.data)).catch(() => setLoadError(true));
  };
  useEffect(load, []);

  const review = (flagId, decision) => {
    setBusyFlag(flagId);
    api.post(`/cascade/flags/${encodeURIComponent(flagId)}/review`, { decision, note: `Reviewed via cascade console — ${decision}` })
      .then((r) => {
        load();
        toast.success(r.data.open_flags_for_event === 0
          ? "Flag reviewed — all dependents reviewed, cascade event resolved."
          : `Flag reviewed — ${r.data.open_flags_for_event} still pending for this event.`);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Review failed — please retry.",
        { action: { label: "Retry", onClick: () => review(flagId, decision) } }))
      .finally(() => setBusyFlag(null));
  };

  return (
    <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid="cascade-panel">
      <div className="flex items-start gap-2">
        <GitBranch className="w-4 h-4 text-teal-600 mt-0.5" aria-hidden="true" />
        <div>
          <h3 className="font-heading text-sm font-bold text-slate-900">Correction cascade &amp; recall</h3>
          <p className="text-xs text-slate-500 mt-0.5 max-w-3xl">{status?.policy || "Source corrections cascade Stale status across every dependent object until reviewed."}</p>
        </div>
      </div>

      {!status && !loadError && (
        <p className="text-[11px] text-slate-400 flex items-center gap-1.5 mt-3" role="status" aria-live="polite" data-testid="cascade-loading">
          <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />Loading cascade status…
        </p>
      )}
      {loadError && (
        <p className="text-[11px] text-red-600 flex items-center gap-2 mt-3" role="alert" data-testid="cascade-load-error">
          Cascade status could not be loaded.
          <Button variant="outline" className="h-6 text-[10px]" onClick={load} data-testid="cascade-retry-btn">Retry</Button>
        </p>
      )}
      {status && status.events.length === 0 && (
        <p className="text-xs text-slate-500 mt-3" data-testid="cascade-no-events">
          No correction events on record. Trigger a cascade from a source's Review tab when it is corrected, withdrawn, retracted or downgraded.
        </p>
      )}

      {status && status.events.length > 0 && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap gap-2" data-testid="cascade-summary">
            {[["events", status.summary.events], ["unresolved", status.summary.unresolved_events],
              ["open flags", status.summary.open_flags], ["reviewed flags", status.summary.reviewed_flags]].map(([l, v]) => (
              <span key={l} className="text-[10.5px] font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-0.5">{v} {l}</span>
            ))}
          </div>
          {status.events.map((ev) => (
            <div key={ev.event_id} className="border border-slate-200 rounded-lg px-3.5 py-2.5" data-testid={`cascade-event-${ev.event_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[10px] text-slate-400 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">{ev.event_id}</span>
                <Badge variant="outline" className={`text-[9px] ${RECALL_BG[ev.recall_status]}`}>{ev.recall_status}</Badge>
                <span className="text-[11px] font-semibold text-slate-800">{ev.source_id} {ev.event}</span>
                <span className="text-[10.5px] text-slate-500">— {ev.reason}</span>
                <Badge variant="outline" className={`text-[9px] ml-auto ${ev.resolved ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                  {ev.resolved ? "Resolved — all dependents reviewed" : "Open — review pending"}</Badge>
              </div>
              <p className="text-[10px] text-slate-500 mt-1">
                {ev.total_flags} dependents flagged Stale · {Object.entries(ev.impact).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k.toLowerCase()}`).join(" · ")}
                {ev.recalled_reports?.length > 0 && ev.recalled_reports.map((r) => (
                  <span key={r.report_id} className="ml-1 text-red-700 font-semibold">
                    · {r.report_id} {r.recall_status.toLowerCase()} → replacement {r.replacement_id}</span>
                ))}
              </p>
            </div>
          ))}

          {status.open_flags.length > 0 && (
            <div className="overflow-x-auto" data-testid="cascade-open-flags">
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1">Stale objects awaiting review — excluded from approved exports</p>
              <table className="w-full text-xs" aria-label="Stale objects awaiting review">
                <thead><tr className="text-left border-b border-slate-200">
                  {["Object", "Type", "Event", "Label", "Review"].map((h) => (
                    <th key={h} scope="col" className="py-1.5 pr-3 font-mono text-[9px] uppercase tracking-widest text-slate-400">{h}</th>))}
                </tr></thead>
                <tbody>
                  {status.open_flags.map((f) => (
                    <tr key={f.flag_id} className="border-b border-slate-100 align-top" data-testid={`cascade-flag-${f.uid}`}>
                      <td className="py-1.5 pr-3 font-mono text-[10px] text-teal-700 whitespace-nowrap">{f.uid}</td>
                      <td className="py-1.5 pr-3 text-slate-500 whitespace-nowrap">{TYPE_LABEL[f.object_type] || f.object_type}</td>
                      <td className="py-1.5 pr-3 font-mono text-[9.5px] text-slate-400 whitespace-nowrap">{f.event_id}</td>
                      <td className="py-1.5 pr-3 text-slate-600 max-w-md leading-snug">{f.label}</td>
                      <td className="py-1.5 pr-3 whitespace-nowrap">
                        <Button variant="outline" className="h-6 text-[10px] mr-1" disabled={busyFlag === f.flag_id}
                          onClick={() => review(f.flag_id, "retained")} data-testid={`flag-retain-${f.uid}`}
                          aria-label={`Mark ${f.uid} reviewed and retained`}>
                          {busyFlag === f.flag_id && <Loader2 className="w-3 h-3 mr-1 animate-spin" aria-hidden="true" />}Reviewed — retain</Button>
                        <Button variant="outline" className="h-6 text-[10px] text-amber-700 border-amber-300" disabled={busyFlag === f.flag_id}
                          onClick={() => review(f.flag_id, "revised")} data-testid={`flag-revise-${f.uid}`}
                          aria-label={`Mark ${f.uid} reviewed with revision`}>Reviewed — revise</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
